/**
 * Timezone formatting utility for displaying user-friendly timezone names
 * Converts IANA timezone identifiers to localized display names
 */

// Map custom locale IDs to standard BCP 47 locale tags
const LOCALE_MAP: Record<string, string> = {
  en: 'en-US',
  cn: 'zh-CN',
  tw: 'zh-TW',
  de: 'de-DE',
  es: 'es-ES',
  fr: 'fr-FR',
  pt: 'pt-BR',
  ja: 'ja-JP',
};

/**
 * Converts custom locale ID to standard BCP 47 locale tag
 * @param customLocale - Custom locale ID (e.g., "en", "cn")
 * @returns Standard BCP 47 locale tag (e.g., "en-US", "zh-CN")
 */
function normalizeLocale(customLocale: string): string {
  return LOCALE_MAP[customLocale] || customLocale;
}

/**
 * Formats a timezone identifier to a user-friendly display name
 * @param timezoneId - IANA timezone identifier (e.g., "America/New_York")
 * @param locale - Custom locale ID (e.g., "en", "cn", "tw") or BCP 47 language tag
 * @returns Formatted timezone name or fallback to original identifier
 */
export function formatTimezone(
  timezoneId: string,
  locale: string = 'en'
): string {
  if (!timezoneId) {
    return '';
  }

  // Normalize the locale to standard BCP 47 format
  const normalizedLocale = normalizeLocale(locale);

  try {
    // Use Intl.DateTimeFormat to get timezone name from a sample date
    const sampleDate = new Date();
    const formatter = new Intl.DateTimeFormat(normalizedLocale, {
      timeZone: timezoneId,
      timeZoneName: 'long',
    });

    const parts = formatter.formatToParts(sampleDate);
    const timeZonePart = parts.find(part => part.type === 'timeZoneName');

    if (timeZonePart) {
      return timeZonePart.value;
    }
  } catch (error) {
    // Fallback if timezone is not supported or other error occurs
    console.warn(`Failed to format timezone ${timezoneId}:`, error);
  }

  // Fallback: Convert underscore to space and capitalize words
  return (
    timezoneId
      .split('/')
      .pop() // Get the last part (e.g., "New_York" from "America/New_York")
      ?.replace(/_/g, ' ') // Replace underscores with spaces
      ?.replace(/\b\w/g, char => char.toUpperCase()) || timezoneId // Capitalize first letter of each word
  );
}

/**
 * Formats a timezone identifier to a short display name
 * @param timezoneId - IANA timezone identifier
 * @param locale - Custom locale ID (e.g., "en", "cn", "tw") or BCP 47 language tag
 * @returns Short timezone name (e.g., "EST", "PST")
 */
export function formatTimezoneShort(
  timezoneId: string,
  locale: string = 'en'
): string {
  if (!timezoneId) {
    return '';
  }

  // Normalize the locale to standard BCP 47 format
  const normalizedLocale = normalizeLocale(locale);

  try {
    if (typeof Intl !== 'undefined' && Intl.DateTimeFormat) {
      const sampleDate = new Date();
      const formatter = new Intl.DateTimeFormat(normalizedLocale, {
        timeZone: timezoneId,
        timeZoneName: 'short',
      });

      const parts = formatter.formatToParts(sampleDate);
      const timeZonePart = parts.find(part => part.type === 'timeZoneName');

      if (timeZonePart) {
        return timeZonePart.value;
      }
    }
  } catch (error) {
    console.warn(`Failed to format timezone ${timezoneId} (short):`, error);
  }

  // Fallback to original identifier
  return timezoneId;
}
